import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ChevronDown, Check, Search } from 'lucide-react';

// Normalise une option (string OU objet) vers { value, label, sublabel, keywords }
const normalize = (opt) => {
  if (opt === null || opt === undefined) return { value: '', label: '', sublabel: null, keywords: '' };
  if (typeof opt === 'string' || typeof opt === 'number') {
    const s = String(opt);
    return { value: s, label: s, sublabel: null, keywords: s.toLowerCase() };
  }
  const value = String(opt.value);
  const label = opt.label != null ? String(opt.label) : value;
  const sublabel = opt.sublabel != null ? String(opt.sublabel) : null;
  const keywords = [value, label, sublabel, opt.keywords]
    .filter(Boolean).join(' ').toLowerCase();
  return { value, label, sublabel, keywords };
};

export const MultiSelect = ({
  label, options = [], selected = [], onChange, testid,
  searchable, searchPlaceholder = 'Rechercher…',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const items = useMemo(() => options.map(normalize), [options]);
  // Recherche activée explicitement, ou automatiquement au-delà de 8 options.
  const showSearch = searchable ?? items.length > 8;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.keywords.includes(q));
  }, [items, query]);

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 justify-between gap-2 rounded-xl bg-card" data-testid={testid}>
          <span className="text-xs text-muted-foreground">{label}</span>
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-5 rounded-full px-1.5 text-[10px]">{selected.length}</Badge>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] max-w-[90vw] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-semibold">{label}</span>
          {selected.length > 0 && (
            <button className="text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => onChange([])} data-testid={`${testid}-clear`}>
              Tout effacer
            </button>
          )}
        </div>

        {showSearch && (
          <div className="border-b p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 rounded-lg pl-8 text-xs"
                data-testid={`${testid}-search`}
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Défilement natif garanti (barre visible + molette) */}
        <div className="max-h-72 overflow-y-auto overscroll-contain p-1.5" data-testid={`${testid}-list`}>
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
              {query ? 'Aucun résultat' : 'Aucune option'}
            </p>
          )}
          {filtered.map((it) => {
            const active = selected.includes(it.value);
            return (
              <button key={it.value} onClick={() => toggle(it.value)}
                className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
                data-testid={`${testid}-opt`}>
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${active ? 'accent-border' : 'border-input'}`}
                  style={active ? { background: 'var(--tenant-accent-500)' } : {}}>
                  {active && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium leading-snug">{it.label}</span>
                  {it.sublabel && (
                    <span className="mt-0.5 block break-words text-[11px] leading-snug text-muted-foreground">
                      {it.sublabel}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {showSearch && items.length > 0 && (
          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground" data-testid={`${testid}-count`}>
            {filtered.length} / {items.length}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
